// The frame catalog (IMPLEMENTATION §2.1 art/catalog.ts, §2.9 "Bake size"): every frame spec, derived from
// the data so new content gets frames without code changes. PURE (no canvas): the M8 generators
// (art/generators.ts) draw each spec; the atlas bake (art/atlas.ts) packs the `atlas` ones.
// Ids are the renderer-facing frame ids of M5 decision 5 (L13), dotted and deterministic:
//   tile.floor / tile.floorAlt / tile.rock          board tiles (checker inside the mask, rock outside)
//   item.<fam>.<tier>                                every family × tier (the tier badge baked in)
//   bld.deposit.<ore>.<L> · bld.<type>.<L>          every building type × level (not chutes)
//   bld.drill.head.<L>                               the drill head overlay (rotated toward the deposit)
//   arrow.<dir>                                      the facing arrow overlaid on machines and drills
//   chute.<in><out> · chev.<out>.<phase>            chute bodies by entry/exit side, chevron phases
//   chevc.<phase>                                    a corner chute's chevrons along its bend (one cell, entry S →
//                                                    exit E; mirrored / rotated at runtime like the body)
//   badge.tier.<t> · badge.stall.<id>               tier badges (pips T1–T4, star T5+) and stall badges
//   target.<kind>                                    Gallery FX targets (juice.json targets.byAmmoTier)
//   bar.bg / bar.fill                                progress bar (cropped, never redrawn)
//   hl.tap / fx.puff                                 tap highlight ring, generic FX puff
//   item.missing                                     fallback for anything the catalog does not know
//   M10 (board FX + render polish): fx.<particle> (spark, dot, star4, coin, ring, chip, confetti.0–2),
//   mp.<type>.<part>[.<i>] (drill bit ×2, press piston, grinder wheel, smelter glow — tweened overlays),
//   item.gun.<t>.bare (the Gallery's mounted gun, no tier badge), mark.dot.0/1 (queue / slot pips) and
//   mark.digit.0–9 (contents counts; vector numerals, never fillText)
// plus frames that are not in the runtime atlas (`atlas: false`: DOM icons, the Unity export, later
// milestones): rock autotile / plate / locked / ghost tiles, markers, Sarge, UI icons, the smoke particle
// (the board uses fx.puff), the Gallery's GUN-LIMITED badge.
import type { Dir } from '../sim/core/types.ts';
import { BTYPES, type BType, VERSIONS, type Version } from '../sim/gen/ids.ts';
import { MOVING_PARTS } from './machines/index.ts';
import { buildPalette } from './palette.ts';
import { gunLayout } from './parts/gun/assemble.ts';
import { seedOf } from './rng.ts';
import type {
  ArtDefs,
  ArtEnv,
  FrameSpec,
  ItemSpec,
  MarkerSpec,
  MovingPartSpec,
  TargetKind,
  UiIconName,
} from './types.ts';

export const DIRS: readonly Dir[] = ['N', 'E', 'S', 'W'];

export const frameId = {
  item: (fam: string, tier: number): string => `item.${fam}.${tier}`,
  building: (type: string, ore: string | null, level: number): string =>
    type === 'deposit' ? `bld.deposit.${ore ?? 'lead'}.${level}` : `bld.${type}.${level}`,
  drillHead: (level: number): string => `bld.drill.head.${level}`,
  arrow: (d: Dir): string => `arrow.${d}`,
  chute: (inDir: Dir, out: Dir): string => `chute.${inDir}${out}`,
  chevron: (d: Dir, phase: number): string => `chev.${d}.${phase}`,
  /**
   * A corner chute's chevrons along the bend, one cell, canonical entry S → exit E (the canonical corner
   * body); the renderer mirrors / turns it like the body (render/sync.ts cornerChevron).
   */
  chevronCorner: (phase: number): string => `chevc.${phase}`,
  tierBadge: (t: number): string => `badge.tier.${t}`,
  stallBadge: (id: string): string => `badge.stall.${id}`,
  target: (kind: string): string => `target.${kind}`,
  missing: 'item.missing',
  floor: 'tile.floor',
  floorAlt: 'tile.floorAlt',
  rock: 'tile.rock',
  barBg: 'bar.bg',
  barFill: 'bar.fill',
  highlight: 'hl.tap',
  puff: 'fx.puff',
  /** M10: the Gallery's mounted gun without its tier badge. */
  gunMount: (tier: number): string => `item.gun.${tier}.bare`,
  /** M10: moving-part overlays (MOVING_PARTS: drill bit 0/1, press piston, grinder wheel, smelter glow). */
  part: (type: string, part: string, index: number): string =>
    part === 'bit' ? `mp.${type}.${part}.${index}` : `mp.${type}.${part}`,
  /** M10: a queue / slot pip (0 = empty, 1 = filled) and a count digit. */
  pip: (full: boolean): string => `mark.dot.${full ? 1 : 0}`,
  digit: (d: number): string => `mark.digit.${d}`,
  fx: (name: string): string => `fx.${name}`,
  /** M10: the Gallery's amber "No ammo" badge (UX §5 GalleryDry: queue empty > galleryDry.afterMs). */
  galleryDry: 'badge.state.noAmmo',
  // not in the runtime atlas
  rockMask: (mask: number): string => (mask === 15 ? 'tile.rock' : `tile.rock.${mask}`),
  ui: (icon: string): string => `ui.${icon}`,
  sarge: (pose: string): string => `sarge.${pose}`,
} as const;

/** M10 particle textures in the runtime atlas (the smoke particle is `fx.puff`). */
export const FX_PARTICLES = ['spark', 'dot', 'star4', 'coin', 'ring', 'chip'] as const;
export const CONFETTI_FRAMES = 3;

export const UI_ICONS: readonly UiIconName[] = [
  'coin',
  'cash',
  'gear',
  'lock',
  'up',
  'star',
  'clock',
  'speakerOn',
  'speakerOff',
  'cart',
  'hand',
  'build',
  'shop',
  'mergeAll',
];

/** Width / height of a count-digit frame (the vector numeral plus its ink stroke; M10 contents counts). */
export const DIGIT_ASPECT = 0.8;

/** Machines whose output side gets a Play notch (UX §6.4). */
const OUTPUT_TYPES: readonly BType[] = [
  'caster',
  'grinder',
  'smelter',
  'press',
  'loader',
  'packer',
  'gunsmith',
];

const vIdx = (v: Version): number => VERSIONS.indexOf(v);
const vMax = (a: Version, b: Version): Version => (vIdx(a) >= vIdx(b) ? a : b);
export const versionActive = (v: Version, content: Version): boolean => vIdx(v) <= vIdx(content);

export interface CatalogOpts {
  /** 'all' = every frame of every version; default: the defs' contentVersion. */
  version?: Version | 'all';
}

export function artEnv(defs: ArtDefs, dpr: number): ArtEnv {
  return { pal: buildPalette(defs.palette), sprites: defs.ui.sprites, dpr };
}

/**
 * A badge's share of the cell: the §2.6 share, raised so the badge is still `minPx` CSS px on the smallest
 * board cell (ui.json layout.cellFloor, L1): tier 34% (≥ 16 px), stall 36% (≥ 18 px).
 */
export function badgeShare(share: number, minPx: number, cellCss: number): number {
  return Math.max(share, minPx / cellCss);
}

/** Levels of a building type (the sim's maxLevelOf; chutes have 1). */
function levelsOf(defs: ArtDefs, type: BType): number {
  const sim = defs.sim;
  switch (type) {
    case 'deposit':
      return sim.depositRules.oddsByLevel.length;
    case 'drill':
      return sim.buildings.drill.hitTicks.length;
    case 'crate':
      return sim.buildings.crate.capacity.length;
    case 'chute':
      return 1;
    case 'gallery':
      return sim.buildings.gallery.gunCapByLevel.length;
    default:
      return sim.machineRules.maxLevel;
  }
}

/** Distinct Gallery target kinds in ammo-tier order with the first tier that shows each. */
export function targetKinds(defs: ArtDefs): { kind: TargetKind; tier: number }[] {
  const out: { kind: TargetKind; tier: number }[] = [];
  defs.juice.targets.byAmmoTier.forEach((k, i) => {
    if (!out.some((x) => x.kind === k)) out.push({ kind: k as TargetKind, tier: i + 1 });
  });
  return out;
}

export function buildCatalog(defs: ArtDefs, opts: CatalogOpts = {}): FrameSpec[] {
  const sim = defs.sim;
  const art = defs.palette.art;
  const sprites = defs.ui.sprites;
  const render = defs.ui.render;
  const cellFloor = defs.ui.layout.cellFloor;
  const gv = art.genVersion;
  const env = artEnv(defs, 1);
  const v01 = VERSIONS[0];
  const out: FrameSpec[] = [];
  // `seedKey` keeps the M8 seeds (keys of the pre-merge scheme), so the reviewed art stays identical
  const base = (
    key: string,
    group: FrameSpec['group'],
    version: Version,
    w: number,
    h: number,
    atlas: boolean,
    seedKey = key,
    px = 0.5,
    py = 0.5,
  ) => ({ key, group, version, w, h, atlas, pivot: { x: px, y: py }, seed: seedOf(seedKey, gv) });

  // board basics (M5 decision 5)
  out.push({
    ...base(frameId.floor, 'tile', v01, 1, 1, true, 'tile/floor/0'),
    gen: 'tile',
    tile: 'floor',
    index: 0,
  });
  out.push({
    ...base(frameId.floorAlt, 'tile', v01, 1, 1, true, 'tile/floor/1'),
    gen: 'tile',
    tile: 'floor',
    index: 1,
  });
  out.push({
    ...base(frameId.rock, 'tile', v01, 1, 1, true, 'tile/rock/15'),
    gen: 'tile',
    tile: 'rock',
    index: 15,
  });
  const mark = (
    key: string,
    mk: MarkerSpec['mark'],
    variant: string,
    version: Version,
    w: number,
    h: number,
    atlas: boolean,
  ) => out.push({ ...base(key, 'marker', version, w, h, atlas), gen: 'marker', mark: mk, variant });
  mark(frameId.highlight, 'highlight', '', v01, 1, 1, true);
  mark(frameId.missing, 'missing', '', v01, 1, 1, true);
  out.push({
    ...base(frameId.puff, 'fx', v01, sprites.puffSize, sprites.puffSize, true),
    gen: 'fx',
    fx: 'smoke',
    index: 0,
  });
  mark(frameId.barBg, 'bar', 'bg', v01, sprites.barWidth, render.barHeight, true);
  mark(frameId.barFill, 'bar', 'fill', v01, sprites.barWidth, render.barHeight, true);

  // items: every family × tier (all versions), tier badge baked in
  const tierShare = badgeShare(sprites.tierBadge, sprites.tierBadgeMinPx, cellFloor);
  let maxTiers = 0;
  for (const f of sim.families) {
    const tiers = f.tierVersions.length;
    maxTiers = Math.max(maxTiers, tiers);
    for (let t = 1; t <= tiers; t++) {
      const version = vMax(f.version, f.tierVersions[t - 1] ?? f.version);
      const spec: ItemSpec = {
        ...base(frameId.item(f.id, t), 'item', version, 1, 1, true, `item/${f.id}/${t}`),
        gen: 'item',
        fam: f.id,
        kind: f.kind,
        tier: t,
        tiers,
        badge: frameId.tierBadge(t),
        badged: true,
        badgeShare: tierShare,
      };
      if (f.id === 'gun') {
        const L = gunLayout(spec, 100, env);
        spec.sockets = {
          muzzle: L.socket.map(([x, y]) => [round4(x / 100), round4(y / 100)] as const),
        };
      }
      out.push(spec);
      // M10: the Gallery mounts the gun without its tier badge (same art, same seed)
      if (f.id === 'gun')
        out.push({ ...spec, key: frameId.gunMount(t), badged: false, group: 'item' });
    }
  }
  // tier badges (separate frames for later overlays): the first version any family reaches the tier
  for (let t = 1; t <= maxTiers; t++) {
    let v: Version | null = null;
    for (const f of sim.families) {
      const tv = f.tierVersions[t - 1];
      if (tv && (v === null || vIdx(vMax(tv, f.version)) < vIdx(v))) v = vMax(tv, f.version);
    }
    out.push({
      ...base(
        frameId.tierBadge(t),
        'badge',
        v ?? v01,
        tierShare,
        tierShare,
        true,
        `badge/tier/${t}`,
      ),
      gen: 'tierBadge',
      tier: t,
    });
  }

  // deposits per ore × level, then every other building type × level (a level exists with its type)
  for (const d of sim.deposits) {
    for (let L = 1; L <= levelsOf(defs, 'deposit'); L++) {
      out.push({
        ...base(
          frameId.building('deposit', d.id, L),
          'building',
          d.version,
          1,
          1,
          true,
          `bld/deposit/${d.id}/${L}`,
        ),
        gen: 'deposit',
        ore: d.id,
        level: L,
      });
    }
  }
  for (const type of BTYPES) {
    if (type === 'deposit' || type === 'chute') continue;
    const b = sim.buildings[type];
    for (let L = 1; L <= levelsOf(defs, type); L++) {
      out.push({
        ...base(
          frameId.building(type, null, L),
          'building',
          b.version,
          1,
          1,
          true,
          `bld/${type}/${L}`,
        ),
        gen: 'building',
        type,
        level: L,
      });
    }
    if (type === 'drill') {
      for (let L = 1; L <= levelsOf(defs, 'drill'); L++) {
        out.push({
          ...base(frameId.drillHead(L), 'building', b.version, 1, 1, true, `bld/drill/head/${L}`),
          gen: 'drillHead',
          level: L,
        });
      }
    }
  }

  // facing arrows, chute bodies (entry × exit), chevron phases
  const chuteV = sim.buildings.chute.version;
  for (const dir of DIRS) {
    mark(frameId.arrow(dir), 'arrow', dir, v01, render.arrowSize, render.arrowSize, true);
  }
  for (const inDir of DIRS) {
    for (const o of DIRS) {
      if (o === inDir) continue;
      out.push({
        ...base(frameId.chute(inDir, o), 'building', chuteV, 1, 1, true),
        gen: 'chute',
        inDir,
        out: o,
      });
    }
  }
  for (const dir of DIRS) {
    for (let p = 0; p < render.chevronPhases; p++) {
      out.push({
        ...base(
          frameId.chevron(dir, p),
          'marker',
          chuteV,
          sprites.chevronSize,
          sprites.chevronSize,
          true,
        ),
        gen: 'chevron',
        dir,
        phase: p,
        phases: render.chevronPhases,
      });
    }
  }
  for (let p = 0; p < render.chevronPhases; p++) {
    out.push({
      ...base(frameId.chevronCorner(p), 'marker', chuteV, 1, 1, true),
      gen: 'chevron',
      dir: 'E',
      phase: p,
      phases: render.chevronPhases,
      inDir: 'S',
    });
  }

  // stall badges (stalls.json: class → shape / colour; glyph null = no badge)
  for (const s of defs.stalls.stalls) {
    const compiled = sim.stalls.find((x) => x.id === s.id);
    if (s.glyph === null || !compiled) continue;
    const cls = defs.stalls.classes[s.class];
    out.push({
      ...base(
        frameId.stallBadge(s.id),
        'badge',
        compiled.version,
        sprites.stallBadge,
        sprites.stallBadge,
        true,
        `badge/stall/${s.id}`,
      ),
      gen: 'stallBadge',
      stall: s.id,
      shape: cls.shape,
      color: cls.color,
      glyph: s.glyph,
      pulseHz: cls.pulseHz,
    });
  }

  // Gallery targets: one per kind of juice.json targets.byAmmoTier (arrives with its first ammo tier)
  const ammo = sim.families.find((f) => f.id === 'ammo');
  for (const { kind, tier } of targetKinds(defs)) {
    const tv = ammo?.tierVersions[tier - 1] ?? v01;
    out.push({
      ...base(
        frameId.target(kind),
        'target',
        tv,
        sprites.targetSize,
        sprites.targetSize,
        true,
        `target/${tier}`,
      ),
      gen: 'target',
      tier,
      kind,
    });
  }

  // ---- not in the runtime atlas yet (DOM icons, catalog.json export, later milestones) ----
  for (const i of [2, 3]) {
    out.push({
      ...base(`tile.floor.speckle.${i - 2}`, 'tile', v01, 1, 1, false, `tile/floor/${i}`),
      gen: 'tile',
      tile: 'floor',
      index: i,
    });
  }
  out.push({
    ...base('tile.plate', 'tile', v01, 1, 1, false, 'tile/plate/0'),
    gen: 'tile',
    tile: 'plate',
    index: 0,
  });
  for (let m = 0; m < 15; m++) {
    out.push({
      ...base(frameId.rockMask(m), 'tile', v01, 1, 1, false, `tile/rock/${m}`),
      gen: 'tile',
      tile: 'rock',
      index: m,
    });
  }
  out.push({
    ...base('tile.locked', 'tile', v01, 1, 1, false, 'tile/locked/0'),
    gen: 'tile',
    tile: 'locked',
    index: 0,
  });
  out.push({
    ...base('tile.ghost', 'tile', v01, 1, 1, false, 'tile/ghost/0'),
    gen: 'tile',
    tile: 'ghost',
    index: 0,
  });

  // moving parts (attach = pivot position in the building cell, in cells; drawn at rest into the bodies)
  const mp = (
    type: BType,
    part: MovingPartSpec['part'],
    index: number,
    w: number,
    h: number,
    px: number,
    py: number,
    ax: number,
    ay: number,
  ) => {
    const old = `mp/${type}/${part}${part === 'bit' ? `/${index}` : ''}`;
    out.push({
      ...base(
        old.replaceAll('/', '.'),
        'part',
        sim.buildings[type as Exclude<BType, 'deposit'>].version,
        w,
        h,
        true, // M10: tweened overlays on the board (drill bit spin, piston stamp, wheel, glow pulse)
        old,
        px,
        py,
      ),
      gen: 'movingPart',
      type,
      part,
      index,
      attach: { x: ax, y: ay },
    });
  };
  for (const s of MOVING_PARTS) mp(s.type, s.part, s.index, s.w, s.h, s.px, s.py, s.ax, s.ay);

  out.push({
    ...base(
      'badge.state.gunLimited',
      'badge',
      v01,
      sprites.stallBadge,
      sprites.stallBadge,
      false,
      'badge/state/gunLimited',
    ),
    gen: 'stallBadge',
    stall: 'GUN_LIMITED',
    shape: 'diamond',
    color: 'state.gunLimited',
    glyph: 'gunUp',
    pulseHz: 0,
  });
  // M10: the Gallery's "No ammo" badge (UX §5 GalleryDry, §6.5 Waiting: amber rounded square, empty tin)
  out.push({
    ...base(
      frameId.galleryDry,
      'badge',
      v01,
      sprites.stallBadge,
      sprites.stallBadge,
      true,
      'badge/state/noAmmo',
    ),
    gen: 'stallBadge',
    stall: 'NO_AMMO',
    shape: defs.stalls.classes.wait.shape,
    color: defs.stalls.classes.wait.color,
    glyph: 'emptyTin',
    pulseHz: 0,
  });
  const rules = defs.palette.rules;
  mark(
    'mark.shadow',
    'shadow',
    '',
    v01,
    rules.contactShadowW + 0.04,
    rules.contactShadowH + 0.04,
    false,
  );
  for (const t of OUTPUT_TYPES) {
    mark(
      `mark.notch.${t}`,
      'notch',
      `machine.${t}`,
      sim.buildings[t as Exclude<BType, 'deposit'>].version,
      art.notch * 1.3,
      art.notch,
      false,
    );
  }
  for (const d of DIRS) mark(`mark.mouth.${d}`, 'mouth', d, v01, 1, 1, false);
  mark('mark.home', 'home', '', v01, 1, 1, false);
  // M10 board overlays: queue / slot pips and the contents-count digits
  mark(frameId.pip(false), 'dot', '0', v01, art.queueDot, art.queueDot, true);
  mark(frameId.pip(true), 'dot', '1', v01, art.queueDot, art.queueDot, true);
  const dh = defs.juice.board.digitH;
  for (let d = 0; d <= 9; d++)
    mark(frameId.digit(d), 'digit', String(d), v01, dh * DIGIT_ASPECT, dh, true);

  for (const pose of ['idle', 'bob', 'blink'] as const) {
    out.push({
      ...base(frameId.sarge(pose), 'sarge', v01, 1, 1, false, `sarge/${pose}`, 0.5, 1),
      gen: 'sarge',
      pose,
    });
  }
  for (const icon of UI_ICONS) {
    out.push({
      ...base(frameId.ui(icon), 'ui', v01, art.uiIconSize, art.uiIconSize, false, `ui/${icon}`),
      gen: 'uiIcon',
      icon,
    });
  }
  for (const fx of ['spark', 'dot', 'star4', 'coin', 'smoke', 'ring', 'chip'] as const) {
    const s = fx === 'ring' ? art.fxSize * 2 : art.fxSize;
    // M10: every particle but the smoke (the board's smoke is fx.puff) is in the runtime atlas
    out.push({
      ...base(
        frameId.fx(fx),
        'fx',
        v01,
        s,
        fx === 'spark' ? s * 1.4 : s,
        fx !== 'smoke',
        `fx/${fx}`,
      ),
      gen: 'fx',
      fx,
      index: 0,
    });
  }
  for (let i = 0; i < CONFETTI_FRAMES; i++) {
    out.push({
      ...base(
        frameId.fx(`confetti.${i}`),
        'fx',
        v01,
        art.fxSize * 0.5,
        art.fxSize * 0.5,
        true,
        `fx/confetti/${i}`,
      ),
      gen: 'fx',
      fx: 'confetti',
      index: i,
    });
  }

  const want = opts.version ?? sim.contentVersion;
  return want === 'all' ? out : out.filter((s) => versionActive(s.version, want));
}

/** The runtime atlas set (what the BootScene bakes): the `atlas` frames of the content version. */
export function atlasCatalog(defs: ArtDefs): FrameSpec[] {
  return buildCatalog(defs).filter((s) => s.atlas);
}

/** Rock autotile mask (`frameId.rockMask`): bit set when that neighbour is also rock (N 1, E 2, S 4, W 8). */
export function rockMask(n: boolean, e: boolean, s: boolean, w: boolean): number {
  return (n ? 1 : 0) | (e ? 2 : 0) | (s ? 4 : 0) | (w ? 8 : 0);
}

/** Rotation (degrees, clockwise) that turns an item-cell gun (muzzle up-right at art.gunAngleDeg) up-screen. */
export function gunUpRotationDeg(gunAngleDeg: number): number {
  return -90 - gunAngleDeg;
}

function round4(x: number): number {
  return Math.round(x * 10000) / 10000;
}

/** catalog.json: the specs without generator internals, keyed for Unity / tools. */
export function catalogJson(specs: readonly FrameSpec[]): Record<string, unknown>[] {
  return specs.map((s) => ({ ...s }));
}
